import { useCallback, useEffect, useMemo, useState } from 'react';
import { UserError } from '@/constants/error';
import {
  EngagementIntent,
  InputMaybe,
  SearchType,
  Talent,
  TalentConnection,
  TalentFilter,
  TalentSort,
  type TalentDetailQuery,
  type TalentListQuery,
} from '@/types/enterprise';
import { type FullTimeSwitchIntent, parseFullTimeSwitchIntents } from '@/constants/talent/talentProfile';
import { api, parseApiErrorMessage } from '@/lib/api/client';

// カスタマーサクセス面談コメント関連のフィールド。共通の Talent 型には無いため交差で補う（詳細/一覧の両方で使用）。
export type TalentCsFields = {
  csComment?: string | null;
  csInterviewed?: boolean;
};

export type TalentListItem = Talent & TalentCsFields;

export type TalentDetailItem = NonNullable<TalentDetailQuery['talent']> & TalentCsFields;

// 共通の TalentFilter に無い絞り込み条件を交差で足す。
export type EnterpriseTalentFilter = TalentFilter & {
  csInterviewedOnly?: boolean;
  // 居住地（都道府県）フィルタ
  residencePrefectureIds?: string[];
  includeResidenceUnset?: boolean;
  nationalityCodes?: string[];
  jlptLevels?: string[];
  foreignNationalityOnly?: boolean;
  spokenLanguageIds?: string[];
  // 正社員意向フィルタ。複数選択は OR。
  fullTimeSwitchIntent?: FullTimeSwitchIntent[];
  // 候補者IDフィルタ（完全一致）
  candidateId?: string;
};

export type TalentListSort = 'priceHourlyAsc' | 'priceHourlyDesc';

// サーバー側の apps/server/src/enterprise/talent/schema.ts の TALENT_MEMO_MAX_LENGTH と同じ制限値。
export const TALENT_MEMO_MAX_LENGTH = 600;

export type TalentListQueryParams = Partial<{
  text: string;
  textMode: string;
  engineerPositionIds: string;
  engineerPositionSearchType: string;
  engineerSkillLanguageIds: string;
  engineerSkillLanguageSearchType: string;
  engineerSkillFrameworkIds: string;
  engineerSkillFrameworkSearchType: string;
  engagementIntents: string;
  priceHourlyMin: string;
  priceHourlyMax: string;
  weeklyWorkingDayMin: string;
  weeklyWorkingDayMax: string;
  ageMin: string;
  ageMax: string;
  residencePrefectureIds: string;
  includeResidenceUnset: string;
  nationalityCodes: string;
  jlptLevels: string;
  foreignNationalityOnly: string;
  spokenLanguageIds: string;
  fullTimeSwitchIntents: string;
  candidateId: string;
}>;

export type TalentListSearchState = {
  sort: TalentListSort;
  immediateOnly: boolean;
  excludeScouted: boolean;
  excludeScoutAgentRequested: boolean;
  csInterviewedOnly: boolean;
};

export const DEFAULT_TALENT_LIST_SORT: TalentListSort = 'priceHourlyAsc';

export const talentListSortSelectItems: { label: string; value: TalentListSort }[] = [
  { label: '希望単価：昇順', value: 'priceHourlyAsc' },
  { label: '希望単価：降順', value: 'priceHourlyDesc' },
];

const toSearchType = (value: string | undefined) => (value === SearchType.And ? SearchType.And : SearchType.Or);

const toTextSearchType = (value: string | undefined) => (value === SearchType.Or ? SearchType.Or : SearchType.And);

const toPositiveNumber = (value: string | undefined) => {
  if (!value) return undefined;

  const parsed = Number(value);
  return parsed > 0 ? parsed : undefined;
};

const toEngagementIntents = (value: string | undefined) => {
  return (
    value
      ?.split(',')
      .filter(Boolean)
      .flatMap((intent) =>
        Object.values(EngagementIntent).includes(intent as EngagementIntent) ? [intent as EngagementIntent] : [],
      ) ?? []
  );
};

const toTalentSort = (sort: TalentListSort) => {
  return sort === 'priceHourlyDesc' ? TalentSort.PriceHourlyDesc : TalentSort.PriceHourlyAsc;
};

export const buildTalentsQueryFilter = (
  queryParams: TalentListQueryParams,
  searchState: TalentListSearchState,
): InputMaybe<EnterpriseTalentFilter> => {
  // 画面は API の enum/input を直接扱わず、この repository 境界で API の filter へ変換する。
  const engagementIntents = toEngagementIntents(queryParams.engagementIntents);
  const trimmedText = queryParams.text?.trim();
  const trimmedCandidateId = queryParams.candidateId?.trim();

  return {
    candidateId: trimmedCandidateId || undefined,
    text: trimmedText || undefined,
    textMode: trimmedText ? toTextSearchType(queryParams.textMode) : undefined,
    engineerPosition: {
      ids: queryParams.engineerPositionIds?.split(',').filter(Boolean) ?? [],
      searchType: toSearchType(queryParams.engineerPositionSearchType),
    },
    engineerSkillLanguage: {
      ids: queryParams.engineerSkillLanguageIds?.split(',').filter(Boolean) ?? [],
      searchType: toSearchType(queryParams.engineerSkillLanguageSearchType),
    },
    engineerSkillFramework: {
      ids: queryParams.engineerSkillFrameworkIds?.split(',').filter(Boolean) ?? [],
      searchType: toSearchType(queryParams.engineerSkillFrameworkSearchType),
    },
    priceHourlyMax: queryParams.priceHourlyMax ? Number(queryParams.priceHourlyMax) : undefined,
    priceHourlyMin: queryParams.priceHourlyMin ? Number(queryParams.priceHourlyMin) : undefined,
    weeklyWorkingDayMax: queryParams.weeklyWorkingDayMax ? Number(queryParams.weeklyWorkingDayMax) : undefined,
    weeklyWorkingDayMin: queryParams.weeklyWorkingDayMin ? Number(queryParams.weeklyWorkingDayMin) : undefined,
    ageMax: toPositiveNumber(queryParams.ageMax),
    ageMin: toPositiveNumber(queryParams.ageMin),
    engagementIntent: searchState.immediateOnly ? [EngagementIntent.ImmediateSearch] : engagementIntents,
    excludeScouted: searchState.excludeScouted,
    excludeScoutAgentRequested: searchState.excludeScoutAgentRequested,
    csInterviewedOnly: searchState.csInterviewedOnly || undefined,
    residencePrefectureIds: queryParams.residencePrefectureIds?.split(',').filter(Boolean) ?? [],
    includeResidenceUnset: queryParams.includeResidenceUnset === 'true' || undefined,
    // 国籍・JLPT のフィルタは非表示（#1151）。企業向け API も受け付けないので、URL に残っていても渡さない。
    // 再表示する場合は、API に検索条件を戻したうえで、下の3行のコメントアウトを外す。
    // nationalityCodes: queryParams.nationalityCodes?.split(',').filter(Boolean) ?? [],
    // jlptLevels: queryParams.jlptLevels?.split(',').filter(Boolean) ?? [],
    // foreignNationalityOnly: queryParams.foreignNationalityOnly === 'true' || undefined,
    spokenLanguageIds: queryParams.spokenLanguageIds?.split(',').filter(Boolean) ?? [],
    fullTimeSwitchIntent: parseFullTimeSwitchIntents(queryParams.fullTimeSwitchIntents),
    sort: toTalentSort(searchState.sort),
  };
};

type TalentListParams = {
  filter?: EnterpriseTalentFilter | null;
  limit: number;
  offset: number;
};

export function useTalentsQuery(params: TalentListParams) {
  const filterKey = JSON.stringify(params.filter ?? null);
  const filter = useMemo(() => JSON.parse(filterKey) as EnterpriseTalentFilter | null, [filterKey]);
  const path = useMemo(
    () => buildTalentListPath({ filter, limit: params.limit, offset: params.offset }),
    [filter, params.limit, params.offset],
  );
  const [state, setState] = useState<{ data?: TalentListQuery; fetching: boolean; error?: Error }>({ fetching: true });
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let canceled = false;
    setState((current) => ({ ...current, fetching: true, error: undefined }));
    api
      .get(path)
      .then((talents: TalentConnection) => {
        if (!canceled) setState({ data: { talents }, fetching: false });
      })
      .catch((error) => {
        if (!canceled)
          setState({
            fetching: false,
            error: error instanceof Error ? error : new Error('候補者一覧の取得に失敗しました'),
          });
      });
    return () => {
      canceled = true;
    };
  }, [reloadKey, path]);

  const refetch = useCallback(() => {
    setReloadKey((key) => key + 1);
  }, []);

  return [state, refetch] as const;
}

export function useTalentQuery(id: string | undefined) {
  const [state, setState] = useState<{ data?: TalentDetailQuery; fetching: boolean; error?: Error }>({
    fetching: !!id,
  });
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    if (!id) return;
    let canceled = false;
    setState((current) => ({ ...current, fetching: true, error: undefined }));
    api
      .get(`/api/v1/enterprise/talents/${id}`)
      .then((talent: Talent) => {
        if (!canceled) setState({ data: { talent }, fetching: false });
      })
      .catch((error) => {
        if (!canceled)
          setState({
            fetching: false,
            error: error instanceof Error ? error : new Error('候補者詳細の取得に失敗しました'),
          });
      });
    return () => {
      canceled = true;
    };
  }, [id, reloadKey]);

  const refetch = useCallback(() => {
    setReloadKey((key) => key + 1);
  }, []);

  return [state, refetch] as const;
}

export function buildTalentListPath({ filter, limit, offset }: TalentListParams) {
  const search = new URLSearchParams();
  search.set('limit', String(limit));
  search.set('offset', String(offset));
  if (filter?.candidateId) search.set('candidateId', String(filter.candidateId));
  if (filter?.text) search.set('text', String(filter.text));
  if (filter?.textMode) search.set('textMode', String(filter.textMode));
  if (filter?.priceHourlyMin != null) search.set('priceHourlyMin', String(filter.priceHourlyMin));
  if (filter?.priceHourlyMax != null) search.set('priceHourlyMax', String(filter.priceHourlyMax));
  if (filter?.weeklyWorkingDayMin != null) search.set('weeklyWorkingDayMin', String(filter.weeklyWorkingDayMin));
  if (filter?.weeklyWorkingDayMax != null) search.set('weeklyWorkingDayMax', String(filter.weeklyWorkingDayMax));
  if (filter?.ageMin != null) search.set('ageMin', String(filter.ageMin));
  if (filter?.ageMax != null) search.set('ageMax', String(filter.ageMax));
  if (filter?.consideringOnly != null) search.set('consideringOnly', String(filter.consideringOnly));
  if (filter?.agreementOnly != null) search.set('agreementOnly', String(filter.agreementOnly));
  if (filter?.excludeScouted != null) search.set('excludeScouted', String(filter.excludeScouted));
  if (filter?.excludeScoutAgentRequested != null)
    search.set('excludeScoutAgentRequested', String(filter.excludeScoutAgentRequested));
  if (filter?.csInterviewedOnly != null) search.set('csInterviewedOnly', String(filter.csInterviewedOnly));
  if (filter?.sort) search.set('sort', String(filter.sort));
  for (const value of filter?.engagementIntent ?? []) {
    if (value) search.append('engagementIntent', String(value));
  }
  appendIds(search, 'engineerPositionIds', filter?.engineerPosition?.ids);
  appendIds(search, 'engineerSkillLanguageIds', filter?.engineerSkillLanguage?.ids);
  appendIds(search, 'engineerSkillFrameworkIds', filter?.engineerSkillFramework?.ids);
  appendIds(search, 'residencePrefectureIds', filter?.residencePrefectureIds);
  appendIds(search, 'nationalityCodes', filter?.nationalityCodes);
  appendIds(search, 'jlptLevels', filter?.jlptLevels);
  appendIds(search, 'spokenLanguageIds', filter?.spokenLanguageIds);
  appendIds(search, 'fullTimeSwitchIntent', filter?.fullTimeSwitchIntent);
  if (filter?.includeResidenceUnset != null) search.set('includeResidenceUnset', String(filter.includeResidenceUnset));
  if (filter?.foreignNationalityOnly != null) search.set('foreignNationalityOnly', String(filter.foreignNationalityOnly));
  if (filter?.engineerPosition?.searchType)
    search.set('engineerPositionSearchType', String(filter.engineerPosition.searchType));
  if (filter?.engineerSkillLanguage?.searchType)
    search.set('engineerSkillLanguageSearchType', String(filter.engineerSkillLanguage.searchType));
  if (filter?.engineerSkillFramework?.searchType)
    search.set('engineerSkillFrameworkSearchType', String(filter.engineerSkillFramework.searchType));
  return `/api/v1/enterprise/talents?${search.toString()}`;
}

function appendIds(search: URLSearchParams, key: string, values?: Array<string | null | undefined> | null) {
  for (const value of values ?? []) {
    if (value) search.append(key, value);
  }
}

export const useTalentMemo = () => {
  const onUpdate = async (talentId: string, memo: string) => {
    try {
      await api.put(`/api/v1/enterprise/talents/${talentId}/memo`, { memo });
    } catch (error) {
      throw new UserError(parseApiErrorMessage(error, 'メモの保存に失敗しました。'));
    }
  };

  return { onUpdate };
};
